import type { ComponentType } from 'react';
import BadgeList from 'src/components/badge-list';
import { notEmpty } from 'src/utilityTypes';

import type { CustomFieldValue } from '@/utils/table/types';
import { matchToArrayField } from '@/utils/table/utils/customAttributeHelpers';
import { resolveUserOrGroupName } from '@/utils/users/resolveUserOrGroup';

import type { CustomAttributeProps } from '../renderers/field-layouts/CustomAttributeProps';
import { CustomAttributeUserMultiSelect } from '../renderers/field-layouts/CustomAttributeUserMultiSelect';
import { getBasicFieldConfig } from './defaults';
import { type FieldTypeConfig } from './types';

export const userMultiselect: FieldTypeConfig = {
  i18nKey: 'customAttributes.fieldTypes.usermultiselect',
  FieldComponent:
    CustomAttributeUserMultiSelect as unknown as ComponentType<CustomAttributeProps>,
  getTableFieldConfig: (renderProps) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const customFieldValue: CustomFieldValue<any> = (
      { CustomAttributeData },
      { userLookup, groupLookup }
    ) => {
      const ids = matchToArrayField(CustomAttributeData, renderProps.path);

      return ids
        ? ids
            .map((id) =>
              resolveUserOrGroupName(id, { userLookup, groupLookup })
            )
            .filter(notEmpty)
        : [];
    };

    return {
      ...getBasicFieldConfig(renderProps),
      customFieldValue,
      isGroupLookupRequired: true,
      // Stable, name-aware sort across user-only, group-only, and mixed
      // payloads. Compare by the first resolved name (lower-cased) so
      // alphabetical ordering ignores casing; fall back to length for ties.
      // Default cell value is a `string[]`, which Cloudscape would
      // otherwise compare via implicit toString — non-deterministic across
      // mixed user+group rows.
      sortingComparator: (a, b) => {
        const aNames = matchToArrayField(a, renderProps.path);
        const bNames = matchToArrayField(b, renderProps.path);
        const aFirst = aNames[0]?.toLocaleLowerCase() ?? '';
        const bFirst = bNames[0]?.toLocaleLowerCase() ?? '';

        if (aFirst !== bFirst) {
          return aFirst.localeCompare(bFirst);
        }

        return aNames.length - bNames.length;
      },
      filterOptions: {
        filteringProperties: {
          operators: [
            {
              operator: '=',
              match: (rowValues: unknown, filterValue: string) =>
                Array.isArray(rowValues) && rowValues.includes(filterValue),
            },
          ],
        },
        filteringOptions: (records) => {
          const userFriendlyNames = Array.from(
            new Set(
              records.flatMap((data) =>
                matchToArrayField(data, renderProps.path)
              )
            )
          );

          return userFriendlyNames.map((name) => ({
            value: name,
            label: name,
          }));
        },
      },
      cell: (data) => {
        const userFriendlyNames = matchToArrayField(data, renderProps.path);

        return <BadgeList isWrapped badges={userFriendlyNames} />;
      },
      exportVal: (data) => matchToArrayField(data, renderProps.path).join(', '),
    };
  },
  getCustomDataSourceFieldDefinition(renderProps) {
    return {
      defaultLabel: renderProps.label,
      displayType: 'badgeList',
      dataType: 'textArray',
    };
  },
  getConditionalPropertyFilterProperty: (_, { users }) => {
    return {
      operators: [
        {
          operator: '=',
          format: (value: string) =>
            users.find((user) => user.Id === value)?.FriendlyName ?? value,
          match: (rowValues: unknown, filterValue: string) =>
            Array.isArray(rowValues) && rowValues.includes(filterValue),
        },
      ],
    };
  },
  getConditionalPropertyFilterOptions: (_, { users }) => {
    return (
      users.map((value) => ({
        value: value.Id!,
        label: value.FriendlyName!,
      })) ?? []
    );
  },
  allowAsConditionSource: true,
};
